import type { DroughtEpisode } from './drought.ts';
import { mean, median, stddev } from './stats.ts';
import { MS_PER_HOUR } from '../shared/constants.ts';

/** Primary displayed spacing: current drought start sequence minus previous long-drought start sequence. */
export const PRIMARY_SPACING_CONVENTION = 'start_to_start_sequence_rounds';

export interface CompletedDroughtRecord {
  lengthRounds: number;
  startSequence: number;
  endSequence: number;
  terminatingHitSequence: number | null;
  startTimestampUtc: string;
  endTimestampUtc: string;
  terminatingHitTimestampUtc: string | null;
  durationMs: number | null;
  segmentId: number;
}

export function toCompletedDroughtRecord(drought: DroughtEpisode): CompletedDroughtRecord {
  return {
    lengthRounds: drought.lengthRounds,
    startSequence: drought.startSequence,
    endSequence: drought.endSequence,
    terminatingHitSequence: drought.terminatingHitSequence,
    startTimestampUtc: drought.startTimestampUtc,
    endTimestampUtc: drought.endTimestampUtc,
    terminatingHitTimestampUtc: drought.terminatingHitTimestampUtc,
    durationMs: drought.absenceUntilHitMs,
    segmentId: drought.segmentId,
  };
}

export function formatSpacingSequence(values: readonly number[]): string {
  return values.map((value) => value.toLocaleString('en-US')).join(' → ');
}

export interface LongDroughtEvent {
  eventIndex: number;
  lengthRounds: number;
  startSequence: number;
  endSequence: number;
  terminatingHitSequence: number | null;
  startTimestampUtc: string;
  endTimestampUtc: string;
  terminatingHitTimestampUtc: string | null;
  durationMs: number | null;
  previousLengthRounds: number | null;
  previousStartSequence: number | null;
  previousEndSequence: number | null;
  /** Primary spacing: start_current - start_previous. Null for the first event or a continuity break. */
  roundsSincePreviousStart: number | null;
  /** Secondary spacing: start_current - end_previous (last miss of the previous long drought). */
  roundsSincePreviousEnd: number | null;
  elapsedMsSincePreviousStart: number | null;
  elapsedMsSincePreviousEnd: number | null;
  hoursSincePreviousStart: number | null;
  targetHitsBetween: number | null;
  spacingObserved: boolean;
  spacingOmittedReason: string | null;
}

export type SpacingSampleSize = 'VERY_SMALL' | 'SMALL' | 'ADEQUATE' | 'NONE';

export interface LongDroughtSpacingStats {
  convention: typeof PRIMARY_SPACING_CONVENTION;
  intervalCount: number;
  mean: number | null;
  median: number | null;
  stddev: number | null;
  minimum: number | null;
  maximum: number | null;
  coefficientOfVariation: number | null;
  meanHours: number | null;
  medianHours: number | null;
  orderedStartToStartRounds: number[];
  sampleSize: SpacingSampleSize;
  sampleSizeNote: string;
}

export interface LongDroughtAnalysis {
  claimKind: 'empirical_historical_frequency';
  note: 'Descriptive locations and spacing of completed droughts with length >= threshold. Not a forecast and not a claim of a repeating pattern.';
  threshold: number;
  inclusive: true;
  primarySpacingConvention: typeof PRIMARY_SPACING_CONVENTION;
  eventCount: number;
  events: LongDroughtEvent[];
  countsByLength: Array<{ droughtLength: number; occurrenceCount: number }>;
  spacing: LongDroughtSpacingStats;
}

export function analyzeLongDroughts(
  completed: readonly CompletedDroughtRecord[],
  hitSequences: readonly number[],
  threshold: number,
): LongDroughtAnalysis {
  if (!Number.isInteger(threshold) || threshold < 1) {
    throw new Error('Long drought threshold must be an integer >= 1.');
  }
  const eventsSource = completed
    .filter((drought) => drought.lengthRounds >= threshold)
    .slice()
    .sort((left, right) => left.startSequence - right.startSequence);
  const hits = [...hitSequences].sort((left, right) => left - right);
  const events: LongDroughtEvent[] = eventsSource.map((drought, index) => {
    const previous = index === 0 ? null : eventsSource[index - 1];
    if (!previous) {
      return toEvent(drought, 1, null, null, true, null);
    }
    const broken = drought.segmentId !== previous.segmentId;
    if (broken) {
      return toEvent(
        drought,
        index + 1,
        previous,
        null,
        false,
        'Spacing omitted: a continuity break separates this drought from the previous long drought.',
      );
    }
    const prevHit = previous.terminatingHitSequence;
    const hitsBetween =
      prevHit === null
        ? null
        : hits.filter((sequence) => sequence > prevHit && sequence < drought.startSequence).length;
    return toEvent(drought, index + 1, previous, hitsBetween, true, null);
  });
  const spacings = events
    .map((event) => event.roundsSincePreviousStart)
    .filter((value): value is number => value !== null);
  const hours = events
    .map((event) => event.hoursSincePreviousStart)
    .filter((value): value is number => value !== null);
  const avg = mean(spacings);
  const spread = stddev(spacings);
  const intervalCount = spacings.length;
  const sampleSize: SpacingSampleSize =
    intervalCount === 0 ? 'NONE' : intervalCount < 5 ? 'VERY_SMALL' : intervalCount < 10 ? 'SMALL' : 'ADEQUATE';
  const sampleSizeNote =
    sampleSize === 'NONE'
      ? 'No observed spacing intervals.'
      : sampleSize === 'VERY_SMALL'
        ? 'Very small spacing sample'
        : sampleSize === 'SMALL'
          ? 'Small spacing sample. No periodicity is inferred.'
          : 'Descriptive spacing only. Visual resemblance is not a repeating or predictive pattern.';
  const maxObserved = events.reduce((max, event) => Math.max(max, event.lengthRounds), 0);
  const countsByLength: Array<{ droughtLength: number; occurrenceCount: number }> = [];
  if (events.length > 0) {
    for (let length = threshold; length <= maxObserved; length += 1) {
      countsByLength.push({
        droughtLength: length,
        occurrenceCount: events.filter((event) => event.lengthRounds === length).length,
      });
    }
  }
  return {
    claimKind: 'empirical_historical_frequency',
    note: 'Descriptive locations and spacing of completed droughts with length >= threshold. Not a forecast and not a claim of a repeating pattern.',
    threshold,
    inclusive: true,
    primarySpacingConvention: PRIMARY_SPACING_CONVENTION,
    eventCount: events.length,
    events,
    countsByLength,
    spacing: {
      convention: PRIMARY_SPACING_CONVENTION,
      intervalCount,
      mean: avg,
      median: median(spacings),
      stddev: spread,
      minimum: spacings.length === 0 ? null : Math.min(...spacings),
      maximum: spacings.length === 0 ? null : Math.max(...spacings),
      coefficientOfVariation: avg !== null && avg > 0 && spread !== null ? spread / avg : null,
      meanHours: mean(hours),
      medianHours: median(hours),
      orderedStartToStartRounds: spacings,
      sampleSize,
      sampleSizeNote,
    },
  };
}

function toEvent(
  drought: CompletedDroughtRecord,
  eventIndex: number,
  previous: CompletedDroughtRecord | null,
  hitsBetween: number | null,
  spacingObserved: boolean,
  spacingOmittedReason: string | null,
): LongDroughtEvent {
  const startMs = Date.parse(drought.startTimestampUtc);
  const previousStartMs = previous ? Date.parse(previous.startTimestampUtc) : Number.NaN;
  const previousEndMs = previous ? Date.parse(previous.endTimestampUtc) : Number.NaN;
  const elapsedStart = spacingObserved && previous && Number.isFinite(startMs) && Number.isFinite(previousStartMs)
    ? startMs - previousStartMs
    : null;
  const elapsedEnd = spacingObserved && previous && Number.isFinite(startMs) && Number.isFinite(previousEndMs)
    ? startMs - previousEndMs
    : null;
  return {
    eventIndex,
    lengthRounds: drought.lengthRounds,
    startSequence: drought.startSequence,
    endSequence: drought.endSequence,
    terminatingHitSequence: drought.terminatingHitSequence,
    startTimestampUtc: drought.startTimestampUtc,
    endTimestampUtc: drought.endTimestampUtc,
    terminatingHitTimestampUtc: drought.terminatingHitTimestampUtc,
    durationMs: drought.durationMs,
    previousLengthRounds: previous?.lengthRounds ?? null,
    previousStartSequence: previous?.startSequence ?? null,
    previousEndSequence: previous?.endSequence ?? null,
    roundsSincePreviousStart:
      spacingObserved && previous ? drought.startSequence - previous.startSequence : null,
    roundsSincePreviousEnd: spacingObserved && previous ? drought.startSequence - previous.endSequence : null,
    elapsedMsSincePreviousStart: elapsedStart,
    elapsedMsSincePreviousEnd: elapsedEnd,
    hoursSincePreviousStart: elapsedStart === null ? null : elapsedStart / MS_PER_HOUR,
    targetHitsBetween: spacingObserved ? hitsBetween : null,
    spacingObserved,
    spacingOmittedReason,
  };
}
